import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdSearch, 
  MdError, 
  MdFilterList, 
  MdCalendarToday, 
  MdCategory,
  MdInventory,
  MdVisibility,
  MdClose,
  MdArrowBack,
  MdWarning,
  MdCheckCircle,
  MdCancel,
  MdDelete,
  MdEdit,
  MdDownload,
  MdScale
} from "react-icons/md";
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

const productTypes = [
  'WW 180',
  'W2 180',
  'WW 210',
  'W2 210',
  'WW 240',
  'W2 240',
  'WW 320',
  'W2 320',
  'WW 360',
  'W2 360',
  'W3',
  'SK1',
  'SK2',
  'SK3',
  'W3M',
  'B1',
  'B2',
  'B3',
  'WW 210s',
  'WW 240s',
  'WW 320s',
  'WW 360s',
  'WW 450',
  'W2 450',
  'WS',
  'WW 450s'
];

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const WholesRecords = () => {
  const navigate = useNavigate();
  
  // State for API data
  const [wholesRecords, setWholesRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    product: '',
    status: '',
    supervisor: ''
  });
  const [userRole, setUserRole] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [downloading, setDownloading] = useState(false);
  const itemsPerPage = 10;

  // Fetch data from API
  const fetchWholesRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('usibras_login');
      const response = await fetch('https://usibras-web-backend.onrender.com/api/analyst-wholes', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.data) {
        setWholesRecords(result.data);
      } else {
        setWholesRecords([]);
      }
    } catch (error) {
      console.error('Error fetching wholes records:', error);
      setError('Failed to load wholes records. Please try again later.');
      setWholesRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Check user role and fetch data on component mount
  useEffect(() => {
    try {
      const token = localStorage.getItem('usibras_login');
      if (token) {
        const decodedToken = jwtDecode(token);
        setUserRole(decodedToken.role);
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      setUserRole(null);
    }

    fetchWholesRecords();
  }, []);

  // Apply filters and search
  const filteredData = wholesRecords.filter(item => {
    const searchTermLower = searchTerm.toLowerCase();
    const searchableValues = [
      item.batch_code,
      item.product,
      item.supervisor,
      item.analysis_date,
      item.assessment_status,
      item.observation,
      item.quality_manager,
      item.rework
    ].filter(Boolean);

    const matchesSearch = searchTermLower === '' || 
      searchableValues.some(value => 
        value.toLowerCase().includes(searchTermLower)
      );

    // Convert date strings to Date objects for comparison
    const itemDate = new Date(item.analysis_date);
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;

    const matchesDate = (!startDate || itemDate >= startDate) &&
                       (!endDate || itemDate <= endDate);
    
    const matchesProduct = !filters.product || item.product === filters.product;
    
    const matchesStatus = !filters.status || item.assessment_status === filters.status;

    const matchesSupervisor = !filters.supervisor || item.supervisor === filters.supervisor;

    return matchesSearch && matchesDate && matchesProduct && matchesStatus && matchesSupervisor;
  });

  // Calculate summary statistics
  const totalRecords = filteredData.length;
  const approvedRecords = filteredData.filter(item => item.assessment_status === 'approved').length;
  const rejectedRecords = filteredData.filter(item => item.assessment_status === 'rejected').length;
  const pendingRecords = filteredData.filter(item => item.assessment_status === 'pending').length;
  const averageMoisture = filteredData.length > 0 
    ? (filteredData.reduce((sum, item) => sum + parseFloat(item.moisture_percentage || 0), 0) / filteredData.length).toFixed(1)
    : '0.0';
  const totalWeight = filteredData.length > 0
    ? filteredData.reduce((sum, item) => sum + parseFloat(item.weigh_kg || 0), 0).toFixed(2)
    : '0.00';

  // Get unique values for filters
  const uniqueProducts = [...new Set(wholesRecords.map(item => item.product))].filter(Boolean);
  const uniqueSupervisors = [...new Set(wholesRecords.map(item => item.supervisor))].filter(Boolean);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const TABLE_HEAD = ["ID", "Batch Code", "Product", "Count", "Monoblocks", "Weight (kg)", "Status", "Rework", "Date", "Time", "Actions"];

  // Format date for display
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  // Format time for display
  const formatTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Format date and time for display
  const formatDateTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return `${dateStr} at ${timeStr}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Format date for input field (YYYY-MM-DD format)
  const formatDateForInput = (dateString) => {
    try {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  };

  // Handle view details
  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setShowModal(true);
  };

  // Handle edit record
  const handleEditRecord = (record) => {
    setRecordToEdit(record);
    setEditFormData({
      batch_code: record.batch_code || '',
      product: record.product || '',
      supervisor: record.supervisor || '',
      quality_manager: record.quality_manager || '',
      analysis_date: formatDateForInput(record.analysis_date),
      count_value: record.count_value || '',
      moisture_percentage: record.moisture_percentage || '',
      weigh_kg: record.weigh_kg || '',
      num_monoblocks: record.num_monoblocks || '',
      assessment_status: record.assessment_status || 'pending',
      automatic_assessment: record.automatic_assessment || '',
      manual_override: record.manual_override || false,
      manual_override_status: record.manual_override_status || '',
      manual_override_reason: record.manual_override_reason || '',
      assessment_message: record.assessment_message || '',
      observation: record.observation || '',
      rework: record.rework || '',
      clean_percentage: record.clean_percentage || '',
      first_quality_percentage: record.first_quality_percentage || '',
      second_quality_percentage: record.second_quality_percentage || '',
      third_quality_percentage: record.third_quality_percentage || '',
      w3d_percentage: record.w3d_percentage || '',
      w3m_percentage: record.w3m_percentage || '',
      broken_percentage: record.broken_percentage || '',
      cut_percentage: record.cut_percentage || '',
      dirty_percentage: record.dirty_percentage || '',
      immature_percentage: record.immature_percentage || '',
      peels_percentage: record.peels_percentage || '',
      rotten_percentage: record.rotten_percentage || '',
      spotted_percentage: record.spotted_percentage || '',
      oily_percentage: record.oily_percentage || ''
    });
    setShowEditModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedRecord(null);
  };

  // Handle delete confirmation
  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!recordToDelete) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem('usibras_login');
      const response = await fetch(`https://usibras-web-backend.onrender.com/api/analyst-wholes/${recordToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove the deleted record from the state
      setWholesRecords(prev => prev.filter(record => record.id !== recordToDelete.id));
      
      // Close the delete modal
      setShowDeleteModal(false);
      setRecordToDelete(null);
      
    } catch (error) {
      console.error('Error deleting record:', error);
      setError('Failed to delete record. Please try again later.');
    } finally {
      setDeleting(false);
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setRecordToDelete(null);
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setRecordToEdit(null);
    setEditFormData({});
  };

  // Handle edit form input changes
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle edit form submission
  const handleEditSubmit = async () => {
    if (!recordToEdit) return;

    try {
      setEditing(true);
      const token = localStorage.getItem('usibras_login');
      
      // Prepare the request payload according to API specification
      const requestPayload = {
        product: editFormData.product || '',
        batch_code: editFormData.batch_code || '',
        supervisor: editFormData.supervisor || '',
        num_monoblocks: parseInt(editFormData.num_monoblocks) || 0,
        count_value: parseInt(editFormData.count_value) || 0,
        moisture_percentage: parseFloat(editFormData.moisture_percentage) || 0,
        weigh_kg: parseFloat(editFormData.weigh_kg) || 0,
        first_quality_percentage: parseFloat(editFormData.first_quality_percentage) || 0,
        second_quality_percentage: parseFloat(editFormData.second_quality_percentage) || 0,
        third_quality_percentage: parseFloat(editFormData.third_quality_percentage) || 0,
        spotted_percentage: parseFloat(editFormData.spotted_percentage) || 0,
        w3m_percentage: parseFloat(editFormData.w3m_percentage) || 0,
        rotten_percentage: parseFloat(editFormData.rotten_percentage) || 0,
        w3d_percentage: parseFloat(editFormData.w3d_percentage) || 0,
        peels_percentage: parseFloat(editFormData.peels_percentage) || 0,
        dirty_percentage: parseFloat(editFormData.dirty_percentage) || 0,
        clean_percentage: parseFloat(editFormData.clean_percentage) || 0,
        immature_percentage: parseFloat(editFormData.immature_percentage) || 0,
        broken_percentage: parseFloat(editFormData.broken_percentage) || 0,
        cut_percentage: parseFloat(editFormData.cut_percentage) || 0,
        oily_percentage: parseFloat(editFormData.oily_percentage) || 0,
        assessment_status: editFormData.assessment_status || 'pending',
        assessment_message: editFormData.assessment_message || '',
        automatic_assessment: editFormData.automatic_assessment || '',
        manual_override: editFormData.manual_override || false,
        manual_override_status: editFormData.manual_override_status || null,
        manual_override_reason: editFormData.manual_override_reason || null,
        observation: editFormData.observation || '',
        rework: editFormData.rework || '',
        analysis_date: editFormData.analysis_date || '',
        quality_manager: editFormData.quality_manager || ''
      };

      const response = await fetch(`https://usibras-web-backend.onrender.com/api/analyst-wholes/${recordToEdit.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.message === 'Record updated successfully') {
        // Update the record in the state
        setWholesRecords(prev => prev.map(record => 
          record.id === recordToEdit.id 
            ? { ...record, ...editFormData }
            : record
        ));
        
        // Close the edit modal
        closeEditModal();
      } else {
        throw new Error('Unexpected response format');
      }
      
    } catch (error) {
      console.error('Error updating record:', error);
      setError('Failed to update record. Please try again later.');
    } finally {
      setEditing(false);
    }
  };

  // Handle download data to Excel
  const handleDownloadData = async () => {
    try {
      setDownloading(true);
      
      // Check if there's filtered data to export
      if (filteredData.length === 0) {
        setError('No data to export. Please adjust your filters.');
        setDownloading(false);
        return;
      }
      
      // Dynamic import of XLSX library
      const XLSX = await import('xlsx');
      
      // Prepare data for Excel export (using filteredData instead of all records)
      const exportData = filteredData.map(record => ({
        'ID': record.id,
        'Batch Code': record.batch_code,
        'Product': record.product,
        'Supervisor': record.supervisor,
        'Quality Manager': record.quality_manager,
        'Analysis Date': formatDate(record.analysis_date),
        'Count Value': record.count_value,
        'Moisture %': record.moisture_percentage,
        'Weight (kg)': record.weigh_kg,
        'Number of Monoblocks': record.num_monoblocks,
        'Assessment Status': record.assessment_status,
        'Automatic Assessment': record.automatic_assessment,
        'Manual Override': record.manual_override ? 'Yes' : 'No',
        'Manual Override Status': record.manual_override_status,
        'Manual Override Reason': record.manual_override_reason,
        'Assessment Message': record.assessment_message,
        'Rework': record.rework,
        'Observation': record.observation,
        'Clean %': record.clean_percentage,
        'First Quality %': record.first_quality_percentage,
        'Second Quality %': record.second_quality_percentage,
        'Third Quality %': record.third_quality_percentage,
        'W3D %': record.w3d_percentage,
        'W3M %': record.w3m_percentage,
        'Broken %': record.broken_percentage,
        'Cut %': record.cut_percentage,
        'Dirty %': record.dirty_percentage,
        'Immature %': record.immature_percentage,
        'Peels %': record.peels_percentage,
        'Rotten %': record.rotten_percentage,
        'Spotted %': record.spotted_percentage,
        'Oily %': record.oily_percentage,
        'Created At': formatDateTime(record.created_at)
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Auto-size columns
      const columnWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Wholes Analysis Records');

      // Generate filename with current date and filter indication
      const currentDate = new Date().toISOString().split('T')[0];
      const hasFilters = filters.startDate || filters.endDate || filters.product || filters.status || filters.supervisor || searchTerm;
      const filename = hasFilters 
        ? `wholes_analysis_records_filtered_${currentDate}.xlsx`
        : `wholes_analysis_records_${currentDate}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

    } catch (error) {
      console.error('Error downloading data:', error);
      setError('Failed to download data. Please try again later.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mt-5 h-full">
      <Card extra={"w-full h-full p-4"}>
        {/* Header with back button */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <button
              onClick={() => navigate(-1)}
              className="flex w-fit items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              <MdArrowBack className="h-5 w-5" />
              Back
            </button>
            <div>
              <h4 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                Wholes Analysis Records
              </h4>
              <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                Detailed view of all wholes analysis records and quality assessments
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDownloadData}
              className="flex w-fit items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600"
              disabled={downloading || loading || filteredData.length === 0}
            >
              {downloading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <MdDownload className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">{downloading ? 'Downloading...' : 'Download Excel'}</span>
            </button>
            <button
              onClick={fetchWholesRecords}
              className="flex w-fit items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              disabled={loading}
            >
              <MdSearch className="h-4 w-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 sm:gap-6">
          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-blue-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdInventory className="h-5 w-5 text-blue-600 dark:text-blue-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Total Records</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{totalRecords}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-green-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdCheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Approved</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{approvedRecords}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-red-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdCancel className="h-5 w-5 text-red-600 dark:text-red-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Rejected</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{rejectedRecords}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-purple-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdCategory className="h-5 w-5 text-purple-600 dark:text-purple-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Avg Moisture</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{averageMoisture}%</p>
              </div>
            </div>
          </Card>

          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-orange-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdScale className="h-5 w-5 text-orange-600 dark:text-orange-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Total Weight</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{totalWeight} kg</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters Section */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-3 dark:border-navy-700 dark:bg-navy-800 sm:p-4">
          <div className="mb-4 flex items-center gap-2">
            <MdFilterList className="h-4 w-4 text-gray-500 sm:h-5 sm:w-5" />
            <h3 className="text-base font-semibold text-gray-700 dark:text-white sm:text-lg">Filters</h3>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5 sm:gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">Product</label>
              <select
                value={filters.product}
                onChange={(e) => setFilters({...filters, product: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              >
                <option value="">All Products</option>
                {uniqueProducts.map((product) => (
                  <option key={product} value={product}>{product}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              >
                <option value="">All Status</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">Supervisor</label>
              <select
                value={filters.supervisor}
                onChange={(e) => setFilters({...filters, supervisor: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              >
                <option value="">All Supervisors</option>
                {uniqueSupervisors.map((supervisor) => (
                  <option key={supervisor} value={supervisor}>{supervisor}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-3 flex justify-end sm:mt-4">
            <button
              onClick={() => setFilters({startDate: '', endDate: '', product: '', status: '', supervisor: ''})}
              className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 sm:px-4 sm:py-2 sm:text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Table header and search */}
        <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:w-auto">
            <div className="flex h-8 items-center rounded-full bg-white px-3 dark:bg-navy-900 sm:h-10 sm:px-4">
              <span className="text-xl">
                <MdSearch className="h-4 w-4 text-gray-400 sm:h-5 sm:w-5" />
              </span>
              <input
                type="text"
                placeholder="Search..."
                className="ml-2 w-full bg-transparent text-xs font-medium text-gray-600 outline-none dark:text-white sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
              <button
                onClick={fetchWholesRecords}
                className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : wholesRecords.length === 0 ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-gray-400">
                <MdInventory className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                No records found
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">
                No wholes analysis records are available at the moment.
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head, index) => (
                    <th
                      key={index}
                      className="border-b border-gray-200 pb-2 pr-2 text-start dark:!border-navy-700 sm:pb-[10px] sm:pr-4"
                    >
                      <p className="text-xs font-bold tracking-wide text-gray-800 dark:text-white sm:text-sm">
                        {head}
                      </p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => (
                  <tr key={item.id}>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.id}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.batch_code}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.product}
                      </p>
                    </td>
                     <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                       <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                         {item.count_value}
                       </p>
                     </td>
                     <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                       <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                         {item.num_monoblocks}
                       </p>
                     </td>
                     <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                       <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                         {item.weigh_kg || 'N/A'}
                       </p>
                     </td>
                     <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium sm:px-2 sm:py-1 ${
                        item.assessment_status === 'approved'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : item.assessment_status === 'rejected'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {item.assessment_status === 'approved' && <MdCheckCircle className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3" />}
                        {item.assessment_status === 'rejected' && <MdCancel className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3" />}
                        {item.assessment_status === 'pending' && <MdWarning className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3" />}
                        <span className="hidden sm:inline">{item.assessment_status}</span>
                        <span className="sm:hidden">
                          {item.assessment_status === 'approved' ? 'OK' : 
                           item.assessment_status === 'rejected' ? 'NO' : 'P'}
                        </span>
                      </span>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium sm:px-2 sm:py-1 ${
                        item.rework === 'Yes'
                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                        {item.rework === 'Yes' && <MdWarning className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3" />}
                        <span className="hidden sm:inline">{item.rework || 'N/A'}</span>
                        <span className="sm:hidden">{item.rework === 'Yes' ? 'Y' : item.rework === 'No' ? 'N' : '-'}</span>
                      </span>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {formatDate(item.analysis_date)}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {formatTime(item.created_at || item.analysis_date)}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <button
                          onClick={() => handleViewDetails(item)}
                          className="rounded-lg bg-blue-100 p-1.5 text-blue-600 hover:bg-blue-200 dark:bg-navy-700 dark:text-blue-400 dark:hover:bg-navy-600 sm:p-2"
                          title="View Details"
                        >
                          <MdVisibility className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                        <button
                          onClick={() => handleEditRecord(item)}
                          className="rounded-lg bg-green-100 p-1.5 text-green-600 hover:bg-green-200 dark:bg-navy-700 dark:text-green-400 dark:hover:bg-navy-600 sm:p-2"
                          title="Edit Record"
                        >
                          <MdEdit className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(item)}
                          className="rounded-lg bg-red-100 p-1.5 text-red-600 hover:bg-red-200 dark:bg-navy-700 dark:text-red-400 dark:hover:bg-navy-600 sm:p-2"
                          title="Delete Record"
                        >
                          <MdDelete className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredData.length > 0 && (
          <div className="mt-4 flex flex-col gap-4 sm:mt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs font-medium text-navy-700 dark:text-white sm:text-sm">
              Showing {paginatedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
            </p>
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === 1
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">First</span>
                <span className="sm:hidden">1</span>
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === 1
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">‹</span>
              </button>
              
              {/* Page number buttons */}
              {[...Array(Math.min(3, totalPages))].map((_, index) => {
                let pageNum;
                if (totalPages <= 3) {
                  pageNum = index + 1;
                } else if (currentPage <= 2) {
                  pageNum = index + 1;
                } else if (currentPage >= totalPages - 1) {
                  pageNum = totalPages - 2 + index;
                } else {
                  pageNum = currentPage - 1 + index;
                }
                
                return (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                      currentPage === pageNum
                        ? "bg-brand-700 text-white"
                        : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === totalPages || totalPages === 0
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">›</span>
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === totalPages || totalPages === 0
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">Last</span>
                <span className="sm:hidden">{totalPages}</span>
              </button>
            </div>
          </div>
                 )}

        {/* Details Modal */}
        {showModal && selectedRecord && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-4 dark:bg-navy-800 sm:p-6">
              {/* Modal Header */}
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3 dark:border-navy-600 sm:mb-6 sm:pb-4">
                <div>
                  <h3 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                    Wholes Analysis Details
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                    Batch Code: {selectedRecord.batch_code}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700 dark:hover:text-white sm:p-2"
                >
                  <MdClose className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
                {/* Basic Information */}
                <div className="space-y-3 sm:space-y-4">
                  <h4 className="text-base font-semibold text-navy-700 dark:text-white sm:text-lg">Basic Information</h4>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Batch Code:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.batch_code}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Product:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.product}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Supervisor:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.supervisor}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Quality Manager:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.quality_manager}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Analysis Date:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{formatDate(selectedRecord.analysis_date)}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Count Value:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.count_value}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Moisture Percentage:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.moisture_percentage}%</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Weight (kg):</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.weigh_kg || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Number of Monoblocks:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.num_monoblocks}</span>
                    </div>
                  </div>
                </div>

                {/* Quality Assessment */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Quality Assessment</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Assessment Status:</span>
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        selectedRecord.assessment_status === 'approved'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : selectedRecord.assessment_status === 'rejected'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {selectedRecord.assessment_status === 'approved' && <MdCheckCircle className="mr-1 h-3 w-3" />}
                        {selectedRecord.assessment_status === 'rejected' && <MdCancel className="mr-1 h-3 w-3" />}
                        {selectedRecord.assessment_status === 'pending' && <MdWarning className="mr-1 h-3 w-3" />}
                        {selectedRecord.assessment_status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Automatic Assessment:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRecord.automatic_assessment || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Manual Override:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRecord.manual_override ? 'Yes' : 'No'}</span>
                    </div>
                    {selectedRecord.manual_override && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Override Status:</span>
                          <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRecord.manual_override_status}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Override Reason:</span>
                          <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRecord.manual_override_reason}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Assessment Message:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRecord.assessment_message || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Rework:</span>
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        selectedRecord.rework === 'Yes'
                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                        {selectedRecord.rework === 'Yes' && <MdWarning className="mr-1 h-3 w-3" />}
                        {selectedRecord.rework || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quality Percentages */}
                <div className="space-y-4 lg:col-span-2">
                  <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Quality Percentages</h4>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Clean</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.clean_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">First Quality</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.first_quality_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Second Quality</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.second_quality_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Third Quality</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.third_quality_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">W3D</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.w3d_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">W3M</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.w3m_percentage}%</p>
                    </div>
                  </div>
                </div>

                {/* Defect Percentages */}
                <div className="space-y-4 lg:col-span-2">
                  <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Defect Percentages</h4>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Broken</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.broken_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Cut</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.cut_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Dirty</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.dirty_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Immature</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.immature_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Peels</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.peels_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Rotten</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.rotten_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Spotted</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.spotted_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Oily</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.oily_percentage}%</p>
                    </div>
                  </div>
                </div>

                {/* Observation */}
                {selectedRecord.observation && (
                  <div className="space-y-4 lg:col-span-2">
                    <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Observation</h4>
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                      <p className="text-sm text-navy-700 dark:text-white">{selectedRecord.observation}</p>
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div className="space-y-4 lg:col-span-2">
                  <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Timestamps</h4>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Created At:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{formatDateTime(selectedRecord.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="mt-4 flex justify-end border-t border-gray-200 pt-3 dark:border-navy-600 sm:mt-6 sm:pt-4">
                <button
                  onClick={closeModal}
                  className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 sm:px-4 sm:py-2 sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && recordToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-md rounded-xl bg-white p-4 dark:bg-navy-800 sm:p-6">
              {/* Modal Header */}
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3 dark:border-navy-600 sm:mb-6 sm:pb-4">
                <div>
                  <h3 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                    Delete Record
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                    This action cannot be undone
                  </p>
                </div>
                <button
                  onClick={closeDeleteModal}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700 dark:hover:text-white sm:p-2"
                >
                  <MdClose className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="mb-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-full bg-red-100 p-2 dark:bg-red-900/20">
                    <MdDelete className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-white">
                      Are you sure you want to delete this record?
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      ID: {recordToDelete.id}
                    </p>
                  </div>
                </div>
                <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/10">
                  <p className="text-xs text-red-700 dark:text-red-300">
                    This will permanently delete the wholes analysis record and all associated data. This action cannot be undone.
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3">
                <button
                  onClick={closeDeleteModal}
                  disabled={deleting}
                  className="flex-1 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  className="flex-1 rounded-lg bg-red-500 px-3 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && recordToEdit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-4 dark:bg-navy-800 sm:p-6">
              {/* Modal Header */}
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3 dark:border-navy-600 sm:mb-6 sm:pb-4">
                <div>
                  <h3 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                    Edit Wholes Analysis Record
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                    ID: {recordToEdit.id} | Batch Code: {recordToEdit.batch_code}
                  </p>
                </div>
                <button
                  onClick={closeEditModal}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700 dark:hover:text-white sm:p-2"
                >
                  <MdClose className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Edit Form */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                  <h4 className="mb-4 text-base font-semibold text-navy-700 dark:text-white sm:text-lg">Basic Information</h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Batch Code</label>
                      <input
                        type="text"
                        value={editFormData.batch_code || ''}
                        onChange={(e) => handleEditFormChange('batch_code', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Product</label>
                      <select
                        value={editFormData.product || ''}
                        onChange={(e) => handleEditFormChange('product', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      >
                        <option value="">Select Product</option>
                        {productTypes.map((product) => (
                          <option key={product} value={product}>{product}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Supervisor</label>
                      <input
                        type="text"
                        value={editFormData.supervisor || ''}
                        readOnly
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed dark:border-navy-600 dark:bg-navy-800 dark:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Quality Manager</label>
                      <input
                        type="text"
                        value={editFormData.quality_manager || ''}
                        readOnly
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed dark:border-navy-600 dark:bg-navy-800 dark:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Analysis Date</label>
                      <input
                        type="date"
                        value={editFormData.analysis_date || ''}
                        onChange={(e) => handleEditFormChange('analysis_date', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Count Value</label>
                      <input
                        type="number"
                        value={editFormData.count_value || ''}
                        onChange={(e) => handleEditFormChange('count_value', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Moisture %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.moisture_percentage || ''}
                        onChange={(e) => handleEditFormChange('moisture_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Weight (kg)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editFormData.weigh_kg || ''}
                        onChange={(e) => handleEditFormChange('weigh_kg', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Number of Monoblocks</label>
                      <select
                        value={editFormData.num_monoblocks || ''}
                        onChange={(e) => handleEditFormChange('num_monoblocks', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      >
                        <option value="">Select Number</option>
                        {Array.from({ length: 30 }, (_, i) => i + 1).map((number) => (
                          <option key={number} value={number}>{number}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Assessment Status</label>
                      <select
                        value={editFormData.assessment_status || 'pending'}
                        onChange={(e) => handleEditFormChange('assessment_status', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Rework</label>
                      <select
                        value={editFormData.rework || ''}
                        onChange={(e) => handleEditFormChange('rework', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      >
                        <option value="">Select Rework Status</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Quality Percentages */}
                <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                  <h4 className="mb-4 text-base font-semibold text-navy-700 dark:text-white sm:text-lg">Quality Percentages</h4>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Clean %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.clean_percentage || ''}
                        onChange={(e) => handleEditFormChange('clean_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">First Quality %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.first_quality_percentage || ''}
                        onChange={(e) => handleEditFormChange('first_quality_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Second Quality %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.second_quality_percentage || ''}
                        onChange={(e) => handleEditFormChange('second_quality_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Third Quality %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.third_quality_percentage || ''}
                        onChange={(e) => handleEditFormChange('third_quality_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">W3D %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.w3d_percentage || ''}
                        onChange={(e) => handleEditFormChange('w3d_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">W3M %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.w3m_percentage || ''}
                        onChange={(e) => handleEditFormChange('w3m_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Defect Percentages */}
                <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                  <h4 className="mb-4 text-base font-semibold text-navy-700 dark:text-white sm:text-lg">Defect Percentages</h4>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Broken %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.broken_percentage || ''}
                        onChange={(e) => handleEditFormChange('broken_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Cut %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.cut_percentage || ''}
                        onChange={(e) => handleEditFormChange('cut_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Dirty %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.dirty_percentage || ''}
                        onChange={(e) => handleEditFormChange('dirty_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Immature %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.immature_percentage || ''}
                        onChange={(e) => handleEditFormChange('immature_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Peels %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.peels_percentage || ''}
                        onChange={(e) => handleEditFormChange('peels_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Rotten %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.rotten_percentage || ''}
                        onChange={(e) => handleEditFormChange('rotten_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Spotted %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.spotted_percentage || ''}
                        onChange={(e) => handleEditFormChange('spotted_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Oily %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.oily_percentage || ''}
                        onChange={(e) => handleEditFormChange('oily_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Assessment Information */}
                <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                  <h4 className="mb-4 text-base font-semibold text-navy-700 dark:text-white sm:text-lg">Assessment Information</h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Automatic Assessment</label>
                      <input
                        type="text"
                        value={editFormData.automatic_assessment || ''}
                        readOnly
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed dark:border-navy-600 dark:bg-navy-800 dark:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Assessment Message</label>
                      <input
                        type="text"
                        value={editFormData.assessment_message || ''}
                        readOnly
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed dark:border-navy-600 dark:bg-navy-800 dark:text-gray-400"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Observation</label>
                      <textarea
                        value={editFormData.observation || ''}
                        onChange={(e) => handleEditFormChange('observation', e.target.value)}
                        rows={3}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-navy-600">
                <button
                  onClick={closeEditModal}
                  disabled={editing}
                  className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  disabled={editing}
                  className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  {editing ? 'Updating...' : 'Update Record'}
                </button>
              </div>
            </div>
          </div>
        )}
       </Card>
     </div>
   );
 };

export default WholesRecords;